import React, { Component } from 'react';
import {
  Alert,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  FormGroup,
  Label,
} from 'reactstrap';

import BooleanField from './fields/BooleanField';
import NumberField from './fields/NumberField';
import TextField from './fields/TextField';
import SelectField from './fields/SelectField';
import JsonField from './fields/JsonField';

const FIELD_COMPONENTS = {
  boolean: BooleanField,
  number: NumberField,
  text: TextField,
  select: SelectField,
  json: JsonField,
};

// Values can be objects (json fields), so compare structurally.
const sameValue = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === 'object' || typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch (err) {
      return false;
    }
  }
  return String(a) === String(b);
};

class SubjectForm extends Component {
  // Current value for a field: an unsaved edit takes precedence, otherwise the
  // persisted value, falling back to the default when nothing is set.
  currentValue(field) {
    const { changed } = this.props;
    const edit = changed.find((c) => c.key === field.key);
    if (edit) return edit.value;
    if (field.value !== undefined) return field.value;
    return field.default;
  }

  hasDefault(field) {
    return Object.prototype.hasOwnProperty.call(field, 'default');
  }

  // The role may read these settings but not write them. Everything that could
  // mutate is turned off — the controls, the change handler, and the buttons —
  // so there is no path from this form to a PATCH/POST.
  get readOnly() {
    return this.props.canUpdate === false;
  }

  handleChange = (key, value) => {
    if (this.readOnly) return;
    this.props.onFieldChange(key, value);
  };

  renderField(field) {
    const FieldComponent = FIELD_COMPONENTS[field.type] || TextField;
    return (
      <FieldComponent
        field={field}
        value={this.currentValue(field)}
        disabled={this.readOnly}
        onChange={this.handleChange}
      />
    );
  }

  renderLabel(field) {
    const value = this.currentValue(field);
    const isDirty = this.props.changed.some((c) => c.key === field.key);
    // persisted: false means nothing is stored and `value` is the declared
    // default — say so, rather than letting it read as a deliberate choice.
    const showUnset = field.persisted === false && !isDirty;
    const canReset =
      !this.readOnly &&
      this.hasDefault(field) &&
      !sameValue(value, field.default);

    return (
      <Label style={{ display: 'block', fontWeight: 600 }}>
        {field.label}
        {field.required && <span style={{ color: '#f5365c' }}> *</span>}
        {showUnset && (
          <Badge
            color="secondary"
            className="ml-2"
            style={{ fontWeight: 400, textTransform: 'none' }}
            title="Nothing is stored for this setting — the declared default applies."
          >
            Not set · using default
          </Badge>
        )}
        {isDirty && (
          <Badge
            color="info"
            className="ml-2"
            style={{ fontWeight: 400, textTransform: 'none' }}
          >
            Edited
          </Badge>
        )}
        {canReset && (
          <Button
            color="link"
            size="sm"
            className="p-0 ml-2"
            style={{ fontWeight: 400, verticalAlign: 'baseline' }}
            onClick={() => this.handleChange(field.key, field.default)}
          >
            Reset to default
          </Button>
        )}
      </Label>
    );
  }

  render() {
    const { subjectData, changed, onSave, onReset, saving, saveError } =
      this.props;
    const { section, subject } = subjectData;
    const readOnly = this.readOnly;
    const hasEdits = changed.length > 0;

    return (
      <Card>
        <CardHeader>
          <h5 className="mb-0">{subject.title}</h5>
          <small className="text-muted">{section.title}</small>
        </CardHeader>
        <CardBody style={{ minHeight: '60vh' }}>
          {saveError && (
            <Alert color="danger" style={{ marginBottom: '20px' }}>
              {saveError}
            </Alert>
          )}

          {/* Say why the form is inert, so it doesn't read as broken. */}
          {readOnly && (
            <Alert color="secondary" style={{ marginBottom: '20px' }}>
              <i
                className="now-ui-icons ui-1_lock-circle-open"
                style={{ marginRight: '8px' }}
              />
              Read-only — your role can view these settings but not change them.
            </Alert>
          )}

          {(subject.groups || []).map((group, gi) => (
            <div key={gi} style={{ marginBottom: '30px' }}>
              {group.title && <h6 className="text-uppercase">{group.title}</h6>}
              <hr style={{ marginTop: '8px' }} />
              {(group.fields || []).map((field) => (
                <FormGroup key={field.key} style={{ marginBottom: '22px' }}>
                  {this.renderLabel(field)}
                  {/* Several descriptions explain routing precedence, so they
                      sit above the control where they get read. */}
                  {field.description && (
                    <p
                      className="text-muted"
                      style={{
                        margin: '0 0 8px',
                        maxWidth: '640px',
                        fontSize: '13px',
                        lineHeight: 1.5,
                        borderLeft: '3px solid #e3e3e3',
                        paddingLeft: '10px',
                      }}
                    >
                      {field.description}
                    </p>
                  )}
                  {this.renderField(field)}
                  <small
                    className="text-muted"
                    style={{ display: 'block', marginTop: '6px' }}
                  >
                    <code>{field.key}</code>
                  </small>
                </FormGroup>
              ))}
            </div>
          ))}

          {!readOnly && (
            <div style={{ marginTop: '20px' }}>
              <Button
                color="primary"
                disabled={!hasEdits || saving}
                onClick={onSave}
              >
                {saving ? 'PLEASE WAIT...' : 'Save changes'}
              </Button>
              <Button
                color="default"
                className="ml-2"
                outline
                disabled={!hasEdits || saving}
                onClick={onReset}
              >
                Reset
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    );
  }
}

export default SubjectForm;
